import { useMutation } from '@apollo/client'

import { SendInternalTransfer } from 'graphql/merchant-portal/mutations/transfers'
import {
  DebitCardTransactionStatusV2,
  FundsTransferFrequency,
  FundsTransferPayeeType,
} from 'types/gql-global-types'
import {
  SendInternalTransfer as SendInternalTransferResponse,
  SendInternalTransferVariables,
} from 'types/gql-types/SendInternalTransfer'
import { ErrorLogger } from 'services/ErrorLogger/ErrorLogger'
import { useZellerTransferValues } from 'pages/Transfer/states/TransferSummary/TransferSummary.hooks'
import { useTransferState } from 'pages/Transfer/Transfer.context'
import { TransferEvent } from 'pages/Transfer/Transfer.stateMachine'

import { useUpdateAfterZellerTransfer } from '../useUpdateAfterZellerTransfer'

const CURRENCY = 'AUD'

export const useSendZellerTransfer = () => {
  const updateAfterZellerTransfer = useUpdateAfterZellerTransfer()
  const { formValues } = useZellerTransferValues()

  const {
    transferState: [, send],
  } = useTransferState()
  const [sendTransfer] = useMutation<
    SendInternalTransferResponse,
    SendInternalTransferVariables
  >(SendInternalTransfer, {
    onCompleted: (response) => {
      const { id, status, transferInUuid } =
        response.transferFundsDebitCardAccount

      if (
        !id ||
        [
          DebitCardTransactionStatusV2.DECLINED,
          DebitCardTransactionStatusV2.CANCELLED,
        ].includes(status)
      ) {
        ErrorLogger.report(
          '[Banking] Mutation response for SendInternalTransfer failed with undefined id or declined/cancelled status'
        )
        send(TransferEvent.Error)
        return
      }

      updateAfterZellerTransfer({
        id,
        transferInUuid,
        from: formValues.from,
        to: formValues.to,
        amountInCents: +formValues.amount * 100,
        reference: formValues.reference,
      })

      send(TransferEvent.Continue)
    },
    onError: () => send(TransferEvent.Error),
  })

  return () => {
    send(TransferEvent.Continue)
    sendTransfer({
      variables: {
        input: {
          payerAccountUuid: formValues.from as string,
          payeeAccountUuid: formValues.to as string,
          amount: {
            value: Math.round(+formValues.amount * 100).toString(),
            currency: CURRENCY,
          },
          payeeReference: formValues.reference,
          frequency: FundsTransferFrequency.IMMEDIATE,
          payeeType: FundsTransferPayeeType.INTERNAL,
        },
      },
    })
  }
}
